/**
 * Typed client for expert_api (myhelp-aidwops repo, expert_api/server.py).
 * Shapes here match that service's real response shapes exactly -- not
 * placeholders. Base URL is env-configurable so a frontend engineer can
 * point this at whatever host expert_api ends up deployed to (currently
 * unresolved -- see the myhelp-ai-server integration question raised
 * this session, which may change where these calls actually go).
 */

import type {
    AskContext,
    AskResponse,
    BatteryDevice,
    ChartSeries,
    DataTable,
    GraphResponse,
    HitlHealth,
    HitlQueueResponse,
    InsightCard,
    IsoDecisionResult,
    IsoQueueResponse,
    ItemDetail,
    Persona,
    PolicyHygieneResponse,
    QueueRequest,
    RefreshResult
} from "./aidwops.types";

const BASE_URL =
    process.env.REACT_APP_EXPERT_API_URL ??
    "https://expert-api.dev-myhelp.eon.com";

function authHeaders(
    accessToken: string,
    extraHeaders?: HeadersInit
): HeadersInit {
    return {
        ...extraHeaders,
        Authorization: `Bearer ${accessToken}`,
    };
}

async function get<T>(
    path: string,
    persona: Persona,
    accessToken: string
): Promise<T> {
    const separator = path.includes("?") ? "&" : "?";
    const res = await fetch(`${BASE_URL}${path}${separator}persona=${persona}`, {
        headers: authHeaders(accessToken),
    });
    if (!res.ok) {
        await throwApiError(path, res);
    }
    return res.json();
}

/**
 * An error answered by expert_api. The backend returns `{"detail": ...}` where detail is
 * a string, an object (`{message, reasons[]}` when a request cannot be applied as asked,
 * `{message, existing_id}` for a duplicate), or a list (FastAPI validation errors).
 */
export class ApiError extends Error {
    readonly status: number;
    /** Why the request cannot be applied as asked (409 with failing pre-flight checks). */
    readonly reasons?: string[];
    /** The item that already holds this request (409 duplicate). */
    readonly existingId?: string;

    constructor(
        message: string,
        status: number,
        extras: { reasons?: string[]; existingId?: string } = {}
    ) {
        super(message);
        // Needed because the TypeScript target is ES5: without it `instanceof ApiError` is false.
        Object.setPrototypeOf(this, ApiError.prototype);
        this.name = "ApiError";
        this.status = status;
        this.reasons = extras.reasons;
        this.existingId = extras.existingId;
    }

    /** 409 without reasons: the item changed since it was read (stale `version`) or is no longer in that state. Reload it. */
    get isStale(): boolean {
        return this.status === 409 && !this.reasons && !this.existingId;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function parseApiDetail(detail: unknown): {
    message: string;
    reasons?: string[];
    existingId?: string;
} {
    if (typeof detail === "string") {
        return { message: detail };
    }

    if (Array.isArray(detail)) {
        const messages = detail
            .map((entry) => {
                if (!isRecord(entry) || typeof entry.msg !== "string") {
                    return null;
                }
                const loc = Array.isArray(entry.loc) ? entry.loc : [];
                const field = loc[loc.length - 1];
                return typeof field === "string"
                    ? `${field}: ${entry.msg}`
                    : entry.msg;
            })
            .filter((message): message is string => message !== null);
        return { message: messages.join(" ") };
    }

    if (isRecord(detail)) {
        const reasons = Array.isArray(detail.reasons)
            ? detail.reasons.filter(
                  (reason): reason is string => typeof reason === "string"
              )
            : undefined;
        const message = typeof detail.message === "string" ? detail.message : "";
        return {
            message: reasons?.length ? reasons.join(" ") : message,
            reasons: reasons?.length ? reasons : undefined,
            existingId:
                typeof detail.existing_id === "string"
                    ? detail.existing_id
                    : undefined,
        };
    }

    return { message: "" };
}

async function throwApiError(path: string, response: Response): Promise<never> {
    const body: unknown = await response.json().catch(() => null);
    const { message, reasons, existingId } = parseApiDetail(
        isRecord(body) ? body.detail : undefined
    );
    throw new ApiError(
        message || response.statusText || `${path} failed (${response.status})`,
        response.status,
        { reasons, existingId }
    );
}

/** A POST that answers JSON; the body, when there is one, goes as JSON. Failures become an `ApiError`. */
async function send<T>(
    name: string,
    path: string,
    accessToken: string,
    body?: unknown
): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
        method: "POST",
        headers: authHeaders(
            accessToken,
            body === undefined ? undefined : { "Content-Type": "application/json" }
        ),
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) {
        await throwApiError(name, res);
    }
    return res.json();
}

const itemPath = (id: string) => `/api/hitl/queue/${encodeURIComponent(id)}`;

export const api = {
    getInsights: (persona: Persona, accessToken: string) =>
        get<InsightCard[]>("/api/insights", persona, accessToken),
    getActions: (persona: Persona, accessToken: string) =>
        get<InsightCard[]>("/api/actions", persona, accessToken),
    getGraph: (persona: Persona, accessToken: string) =>
        get<GraphResponse>("/api/graph", persona, accessToken),
    getPrompts: (persona: Persona, accessToken: string) =>
        get<string[]>("/api/prompts", persona, accessToken),
    getQueue: (
        persona: Persona,
        accessToken: string,
        status: "waiting" | "decided" | "all" | "inflight" | "finished" = "waiting"
    ) =>
        get<HitlQueueResponse>(
            `/api/hitl/queue?status=${status}`,
            persona,
            accessToken
        ),
    /** Asks the Zscaler and Intune MCP servers for current findings. `force` skips the 5-minute minimum between refreshes. */
    refreshQueue: async (
        accessToken: string,
        force = false
    ): Promise<RefreshResult> => {
        const res = await fetch(
            `${BASE_URL}/api/hitl/queue/refresh${force ? "?force=true" : ""}`,
            {
                method: "POST",
                headers: authHeaders(accessToken),
            }
        );
        if (!res.ok) {
            await throwApiError("refreshQueue", res);
        }
        return res.json();
    },
    getQueueItem: async (id: string, accessToken: string): Promise<ItemDetail> => {
        const res = await fetch(
            `${BASE_URL}/api/hitl/queue/${encodeURIComponent(id)}`,
            {
                headers: authHeaders(accessToken),
            }
        );
        if (!res.ok) {
            await throwApiError("getQueueItem", res);
        }
        return res.json();
    },
    decideQueueItem: async (
        id: string,
        decision: "approve" | "decline",
        reason: string,
        version: number,
        accessToken: string
    ): Promise<ItemDetail> => {
        const res = await fetch(
            `${BASE_URL}/api/hitl/queue/${encodeURIComponent(id)}/decision`,
            {
                method: "POST",
                headers: authHeaders(accessToken, {
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({
                    decision,
                    reason,
                    confirmed: true,
                    version,
                }),
            }
        );
        if (!res.ok) {
            await throwApiError("decideQueueItem", res);
        }
        return res.json();
    },
    rollbackQueueItem: async (
        id: string,
        reason: string,
        version: number,
        accessToken: string
    ): Promise<ItemDetail> => {
        const res = await fetch(
            `${BASE_URL}/api/hitl/queue/${encodeURIComponent(id)}/rollback`,
            {
                method: "POST",
                headers: authHeaders(accessToken, {
                    "Content-Type": "application/json",
                }),
                body: JSON.stringify({ reason, version }),
            }
        );
        if (!res.ok) {
            await throwApiError("rollbackQueueItem", res);
        }
        return res.json();
    },
    raiseWhitelistRequest: async (
        url: string,
        justification: string,
        accessToken: string
    ): Promise<ItemDetail> => {
        const res = await fetch(`${BASE_URL}/api/hitl/queue`, {
            method: "POST",
            headers: authHeaders(accessToken, {
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                url,
                justification,
                kind: "whitelist_request",
            }),
        });
        if (!res.ok) {
            await throwApiError("raiseWhitelistRequest", res);
        }
        return res.json();
    },
    raiseSslBypassRequest: async (
        url: string,
        justification: string,
        accessToken: string
    ): Promise<ItemDetail> => {
        const res = await fetch(`${BASE_URL}/api/hitl/queue`, {
            method: "POST",
            headers: authHeaders(accessToken, {
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                url,
                justification,
                kind: "ssl_bypass_request",
            }),
        });
        if (!res.ok) {
            await throwApiError("raiseSslBypassRequest", res);
        }
        return res.json();
    },
    /** After `apply_failed`: tries the write to Zscaler again. The checks are re-read first. */
    retryQueueItem: (id: string, accessToken: string): Promise<ItemDetail> =>
        send("retryQueueItem", `${itemPath(id)}/retry`, accessToken),
    /** The last step of a request that was written to Zscaler: a closing remark (12+ characters) goes on the Jira issue and the issue is closed. */
    closeQueueItem: (
        id: string,
        remark: string,
        version: number,
        accessToken: string
    ): Promise<ItemDetail> =>
        send("closeQueueItem", `${itemPath(id)}/close`, accessToken, {
            remark,
            version,
        }),
    /** A note on the request's Jira issue, prefixed with who wrote it. It does not change the request, so its `version` stays. */
    commentOnQueueItem: (
        id: string,
        text: string,
        accessToken: string
    ): Promise<ItemDetail> =>
        send("commentOnQueueItem", `${itemPath(id)}/comment`, accessToken, {
            text,
        }),
    /** Raises a whitelist or SSL-bypass request. Applies nothing: it creates a queue item with its pre-flight. A duplicate answers 409 with `existing_id`. */
    createQueueItem: (
        url: string,
        justification: string,
        kind: "whitelist_request" | "ssl_bypass_request",
        accessToken: string
    ): Promise<ItemDetail> =>
        send("createQueueItem", "/api/hitl/queue", accessToken, {
            url,
            justification,
            kind,
        }),
    getIsoQueue: async (
        group: "pending" | "decided" | "all",
        accessToken: string
    ): Promise<IsoQueueResponse> => {
        const res = await fetch(`${BASE_URL}/api/hitl/iso/queue?group=${group}`, {
            headers: authHeaders(accessToken),
        });
        if (!res.ok) {
            await throwApiError("getIsoQueue", res);
        }
        return res.json();
    },
    getIsoItem: async (id: string, accessToken: string): Promise<ItemDetail> => {
        const res = await fetch(
            `${BASE_URL}/api/hitl/iso/${encodeURIComponent(id)}`,
            { headers: authHeaders(accessToken) }
        );
        if (!res.ok) {
            await throwApiError("getIsoItem", res);
        }
        return res.json();
    },
    /** The ISO's decision. Rejecting needs a reason of 12+ characters; approving does not. Only a listed ISO approver may call it. */
    decideIsoItem: (
        id: string,
        decision: "approve" | "reject",
        reason: string,
        version: number,
        accessToken: string
    ): Promise<IsoDecisionResult> =>
        send(
            "decideIsoItem",
            `/api/hitl/iso/${encodeURIComponent(id)}/decision`,
            accessToken,
            { decision, reason, confirmed: true, version }
        ),
    getHitlHealth: async (accessToken: string): Promise<HitlHealth> => {
        const res = await fetch(`${BASE_URL}/api/hitl/health`, {
            headers: authHeaders(accessToken),
        });
        if (!res.ok) {
            await throwApiError("getHitlHealth", res);
        }
        return res.json();
    },
    /** The ten needs-attention devices with the lowest remaining battery capacity, lowest first. Read from the index, never from Graph. */
    getWorstBatteryDevices: async (
        accessToken: string
    ): Promise<BatteryDevice[]> => {
        const res = await fetch(
            `${BASE_URL}/api/intune/battery/worst-devices`,
            { headers: authHeaders(accessToken) }
        );
        if (!res.ok) {
            await throwApiError("getWorstBatteryDevices", res);
        }
        const body: unknown = await res.json();
        return isRecord(body) && Array.isArray(body.devices)
            ? (body.devices as BatteryDevice[])
            : [];
    },
    /** The full list of unassigned/superseded/duplicate/redundant policy findings, named. Read-only; no remediation is performed. */
    getPolicyHygiene: async (
        accessToken: string
    ): Promise<PolicyHygieneResponse> => {
        const res = await fetch(`${BASE_URL}/api/intune/policy-hygiene`, {
            headers: authHeaders(accessToken),
        });
        if (!res.ok) {
            await throwApiError("getPolicyHygiene", res);
        }
        const body: unknown = await res.json();
        return {
            rows: isRecord(body) && Array.isArray(body.rows) ? body.rows : [],
            summary: isRecord(body) && isRecord(body.summary) ? (body.summary as Record<string, number>) : null,
            total_policies: isRecord(body) ? ((body.total_policies as number | null) ?? null) : null,
            compliance_policy_count: isRecord(body)
                ? ((body.compliance_policy_count as number | null) ?? null)
                : null,
            configuration_profile_count: isRecord(body)
                ? ((body.configuration_profile_count as number | null) ?? null)
                : null,
            settings_catalog_policy_count: isRecord(body)
                ? ((body.settings_catalog_policy_count as number | null) ?? null)
                : null,
            settings_catalog_error: isRecord(body)
                ? ((body.settings_catalog_error as string | null) ?? null)
                : null,
            settings_catalog_assignment_unavailable: isRecord(body)
                ? ((body.settings_catalog_assignment_unavailable as boolean | null) ?? null)
                : null,
        };
    },
    getCharts: (persona: Persona, accessToken: string) =>
        get<ChartSeries[]>("/api/charts", persona, accessToken),
    getTables: (persona: Persona, accessToken: string) =>
        get<DataTable[]>("/api/tables", persona, accessToken),

    ask: async (
        question: string,
        accessToken: string,
        previousResponseId?: string,
        context?: AskContext
    ): Promise<AskResponse> => {
        const res = await fetch(`${BASE_URL}/api/ask`, {
            method: "POST",
            headers: authHeaders(accessToken, {
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                question,
                previous_response_id: previousResponseId,
                ...(context ? { context } : {}),
            }),
        });
        if (!res.ok) {
            await throwApiError("ask", res);
        }
        return res.json();
    },
};
